import { z } from 'zod';

import { generateMock } from '../../../../utils/generate-mock';
import { mileageSchema } from '../../../../schema/mileage';

import { THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_CLOSE_BROTHERS } from '../../financeProviders/closebrothers/panels/ResidentialDetails/constants';
import { THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_BOI } from '../../financeProviders/bankofireland/panels/ResidentialDetails/constants';
import { THRESHOLD_TO_SHOW_PREVIOUS_EMPLOYMENT_FIELDS_IN_YEARS } from '../../financeProviders/bankofireland/panels/EmploymentDetails/constants';

const providerSchema = z.union([
  z.literal('closebrothers'),
  z.literal('bankofireland'),
]);

/**
 * The phone number field is accompanied with a disabled select dropdown representing the phone extension, purely for display purposes.
 * Its selected value is `+353`. The BE expects the phone number to be in this format `0871234567`.
 * 353` and `+353` result in validation errors. Hence the phone number is transformed from `87...` to `087...`.
 **/
const phoneNumberFormatter = (value: string) =>
  value.startsWith('0') ? value : `0${value}`;
const queryParameterSchema = z.object({
  dealerId: z.string(),
  dealerName: z.string(),
  adId: z.string(),
  provider: providerSchema,
  make: z.string(),
  model: z.string(),
  variant: z.ostring(),
  mileage: z.ostring(),
  mileageType: mileageSchema.optional(),
  year: z.string(),
  // used in calculations
  price: z.coerce.number(),
  sellerType: z.string(),
  userId: z.ostring(),
  minimumDepositValue: z.string(),
  minimumDepositPercentage: z.string(),
  /**
   * Temporary fix to handle malformed iOS links where platform appears twice, resulting
   * in an array rather than a string
   */
  platform: z.string().or(z.array(z.string())).optional(),
  motorCategory: z.ostring(),
  _gl: z.ostring(),
});

const mappedQueryParameterSchema = queryParameterSchema.transform(
  ({ provider, year, variant, price, ...data }) => ({
    ...data,
    price: price.toString(),
    financeHouse: provider,
    carYear: year,
    carVariant: variant,
  }),
);

const hiddenFieldsSchema = z.object({
  provider: z.string(),
  dealerId: z.string(),
  dealerName: z.string(),
  dealerEmail: z.ostring(),
  financeEmail: z.ostring().nullable(),
  adId: z.string(),
  contentUrl: z.ostring(),
  clickSource: z.ostring(),
  sellerType: z.string(),
  platform: z.ostring(),
  clientUid: z.ostring(),
  motorCategory: z.string(),
});

const loanDetailsSchema = z.object({
  minimumDepositValue: z.string(),
  minimumDepositPercent: z.string(),
  pricePerMonth: z.number(),
  aprRate: z.number(),
  monthlyInstallments: z.number(),
  monthlyInstallmentOptions: z.array(
    z.object({ displayValue: z.string(), value: z.number() }),
  ),
  interestRate: z.number(),
  totalFees: z.number(),
  minimumFinanceAmount: z.number(),
  documentationFees: z.number(),
  purchaseInstallmentFees: z.number(),
  disclaimerText: z.ostring(),
});

const REQUIRED_FIELD = 'This field is required';

const requiredString = z.string({ message: REQUIRED_FIELD }).min(1, {
  message: REQUIRED_FIELD,
});
const requiredNumber = (minimumLenghtErrorMessage: string) =>
  z.number({ message: REQUIRED_FIELD }).min(1, {
    message: minimumLenghtErrorMessage,
  });
const requiredBooleanValueTrue = z.boolean().refine((value) => value, {
  message: REQUIRED_FIELD,
});

const loanDetailsUserInputSchema = z.object({
  cashDeposit: requiredNumber('Cash deposit must be greater than zero'),
  tradeInValue: z.number().optional(),
  totalDeposit: requiredNumber('Total deposit must be greater than zero'),
  financeAmount: requiredNumber('Finance amount must be greater than zero'),
});

const sharedLoanDetailsSchema = loanDetailsSchema.merge(
  loanDetailsUserInputSchema,
);

const carDetailsSchema = z.object({
  make: z.string(),
  model: z.string(),
  variant: z.string(),
  year: z.number(),
  mileage: z.ostring(),
  mileageType: z.ostring(),
  price: z.number(),
  registrationNumber: z.ostring(),
});

const carFinanceInitialFormDataSchema = z.object({
  hiddenFields: hiddenFieldsSchema,
  loanDetails: loanDetailsSchema,
  carDetails: carDetailsSchema,
});

const mockCarFinanceInitialFormData = generateMock(
  carFinanceInitialFormDataSchema,
  {
    hiddenFields: {
      provider: 'closebrothers',
      dealerId: '2088',
      dealerName: 'Auto Accelerator Dealer (dd4) upt',
      adId: '15300073',
      sellerType: 'NONE',
      platform: '',
      motorCategory: 'Cars',
    },
    loanDetails: {
      minimumDepositValue: '48422',
      minimumDepositPercent: '20',
      pricePerMonth: 5420,
      aprRate: 9.99,
      monthlyInstallments: 42,
      monthlyInstallmentOptions: [
        {
          displayValue: '2 years',
          value: 24,
        },
        {
          displayValue: '3 years',
          value: 36,
        },
      ],
      interestRate: 9.29,
      totalFees: 150,
      minimumFinanceAmount: 5000,
      documentationFees: 75,
      purchaseInstallmentFees: 0,
    },
    carDetails: {
      make: 'Ford',
      model: 'Focus',
      variant: 'FOCUS EDITION 1.6 95BHP 2014MY',
      year: 2014,
      mileage: '24242',
      mileageType: 'Kilometres',
      price: 242111,
    },
  },
);

const loanRequestSchema = loanDetailsSchema.extend({
  cashDeposit: z.number().default(0),
  totalDeposit: z.number().default(0),
  financeAmount: z.number().default(0),
  financeHouse: providerSchema,
  dealerId: z.string(),
});

const loanResponseSchema = z.object({
  costOfCredit: z.number(),
  financeAmount: z.number(),
  monthlyInstallments: z.number(),
  pricePerMonth: z.number(),
  totalDeposit: z.number(),
  totalPayable: z.number(),
  apr: z.number(),
  interest: z.number(),
  disclaimer: z.string().nullable(),
  warningEarlyRepayment: z.string().nullable(),
  warningMissedRepayments: z.string().nullable(),
});

const mockLoanResponse = generateMock(loanResponseSchema, {
  costOfCredit: 10000,
  financeAmount: 8000,
  monthlyInstallments: 54,
  pricePerMonth: 200,
  totalDeposit: 2000,
  totalPayable: 0,
  apr: 9.99,
  interest: 0,
  disclaimer: 'This is a disclaimer',
  warningEarlyRepayment: 'This is a warning',
  warningMissedRepayments: 'This is another warning',
});

const sharedPersonalDetailsSchema = z.object({
  title: z.string({ message: 'Title cannot be empty' }).min(1, {
    message: 'Title cannot be empty',
  }),
  firstName: z.string({ message: 'First name cannot be empty' }).min(1, {
    message: 'First name cannot be empty',
  }),
  surname: z.string({ message: 'Surname cannot be empty' }).min(1, {
    message: 'Surname cannot be empty',
  }),
  phone: z
    .string({ message: 'Please enter a valid phone number' })
    .min(1, {
      message: 'Phone number cannot be empty',
    })
    .transform(phoneNumberFormatter),
  email: z
    .string({ message: 'Please enter an email address' })
    .email({ message: 'Please enter a valid email address' }),
  dateOfBirth: z.date({ message: 'Date of birth cannot be empty' }),
  maritalStatus: z
    .string({ message: 'Marital status cannot be empty' })
    .min(1, {
      message: 'Marital status cannot be empty',
    }),
  ppsNumber: z.string({ message: 'Please enter a valid PPS number' }).min(1, {
    message: 'Please enter a valid PPS number',
  }),
  gender: z.string({ message: 'Gender cannot be empty' }).min(1, {
    message: 'Gender cannot be empty',
  }),
});

const personalDetailsSchemaCloseBrothers = sharedPersonalDetailsSchema.extend({
  countryOrigin: z
    .string({ message: 'County of birth cannot be empty' })
    .min(1, {
      message: 'County of birth cannot be empty',
    }),
  maidenName: z.string().optional(),
  // BE only accepts the american english version of "dependants"
  numberOfDependents: z
    .string({
      message: 'Number of dependants cannot be empty',
    })
    .min(1, {
      message: 'Number of dependants cannot be empty',
    }),
  tradeInRegPlate: z.string().optional(),
});

const sharedCurrentAddressSchema = z.object({
  residentialStatus: requiredString,
  addressLine1: requiredString,
  county: requiredString,
  timeAtCurrentAddress: z.object({
    years: requiredString,
  }),
});

const sharedPreviousAddressSchema = z.object({
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  county: z.string().optional(),
});

const sharedEmploymentDetailsSchema = z.object({
  occupation: requiredString,
  status: requiredString,
  employerName: requiredString,
  timeWithEmployer: z.object({
    years: requiredString,
  }),
});

const sharedBankingDetailsSchema = z.object({
  income: requiredNumber('Income must be greater than zero'),
  monthlyMortgageRent: z.number({ message: REQUIRED_FIELD }),
  bankName: requiredString,
});

const sharedConsentSchema = z.object({
  dataConsent: requiredBooleanValueTrue,
});

const closebrothersFormSchema = z
  .object({
    carDetails: carDetailsSchema,
    hiddenFields: hiddenFieldsSchema,
    loanDetails: sharedLoanDetailsSchema.extend({
      outstandingLoan: z.number().optional(),
      monthlyInstallments: requiredNumber(
        'Monthly installments must be greater than zero',
      ),
    }),
    personalDetails: personalDetailsSchemaCloseBrothers,
    currentAddress: sharedCurrentAddressSchema.extend({
      addressLine2: z.string().optional(),
      town: requiredString,
      eircode: z.string().optional(),
    }),
    previousAddress: sharedPreviousAddressSchema
      .extend({
        town: z.string().optional(),
        eircode: z.string().optional(),
        timeAtAddress: z.object({
          years: z.string().optional(),
        }),
      })
      .optional(),
    employmentDetails: sharedEmploymentDetailsSchema.extend({
      industry: requiredString,
      type: requiredString,
      employerPhone: requiredString.transform(phoneNumberFormatter),
      employerAddress: z.object({
        addressLine1: requiredString,
        addressLine2: z.string().optional(),
        town: requiredString,
        county: requiredString,
        eircode: z.string().optional(),
      }),
    }),
    bankingDetails: sharedBankingDetailsSchema.extend({
      accountType: requiredString,
      accountHolder: z.string().optional(),
      yearsWithBank: requiredString,
    }),
    consent: sharedConsentSchema,
  })
  .superRefine(({ currentAddress, previousAddress }, ctx) => {
    /**
     * previousAddress is a conditionally-rendered field.
     *
     * We compare it to currentAddress.timeAtCurrentAddress.years to deterine when to trigger a validation error.
     */
    const displayPreviousAddress =
      Number(currentAddress.timeAtCurrentAddress.years) <=
      THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_CLOSE_BROTHERS;

    if (
      displayPreviousAddress &&
      (previousAddress?.addressLine1 === undefined ||
        previousAddress?.addressLine1.length < 1)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_FIELD,
        path: ['previousAddress.addressLine1'],
      });
    }

    if (
      displayPreviousAddress &&
      (previousAddress?.county === undefined ||
        previousAddress?.county.length < 1)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_FIELD,
        path: ['previousAddress.county'],
      });
    }

    if (
      displayPreviousAddress &&
      (previousAddress?.town === undefined || previousAddress?.town.length < 1)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_FIELD,
        path: ['previousAddress.town'],
      });
    }

    if (
      displayPreviousAddress &&
      (previousAddress?.timeAtAddress.years === undefined ||
        previousAddress?.timeAtAddress.years.length < 1)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_FIELD,
        path: ['previousAddress.timeAtAddress.years'],
      });
    }
  });

const bankofirelandFormSchema = z
  .object({
    carDetails: carDetailsSchema,
    hiddenFields: hiddenFieldsSchema,
    loanDetails: sharedLoanDetailsSchema,
    personalDetails: sharedPersonalDetailsSchema,
    currentAddress: sharedCurrentAddressSchema.extend({
      addressLine2: requiredString,
      addressLine3: z.string().optional(),
    }),
    previousAddress: sharedPreviousAddressSchema
      .extend({
        addressLine3: z.string().optional(),
      })
      .optional(),
    employmentDetails: sharedEmploymentDetailsSchema,
    previousEmploymentDetails: z
      .object({
        status: z.string().optional(),
        previousOccupation: z.string().optional(),
        previousEmployerName: z.string().optional(),
        timeWithPreviousEmployer: z.object({
          years: z.string().optional(),
        }),
      })
      .optional(),
    bankingDetails: sharedBankingDetailsSchema,
    accountDetails: z
      .object({
        sortCode: z.string().optional(),
        accountNumber: z.string().optional(),
      })
      .optional(),
    consent: sharedConsentSchema.extend({
      acceptDocuments: requiredBooleanValueTrue,
    }),
  })
  .superRefine(
    (
      {
        currentAddress,
        previousAddress,
        employmentDetails,
        previousEmploymentDetails,
      },
      ctx,
    ) => {
      /**
       * previousAddress is a conditionally-rendered field.
       *
       * We compare it to currentAddress.timeAtCurrentAddress.years to deterine when to trigger a validation error.
       */
      const displayPreviousAddress =
        Number(currentAddress.timeAtCurrentAddress.years) <=
        THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_BOI;

      if (
        displayPreviousAddress &&
        (previousAddress?.addressLine1 === undefined ||
          previousAddress?.addressLine1.length < 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_FIELD,
          path: ['previousAddress.addressLine1'],
        });
      }

      if (
        displayPreviousAddress &&
        (previousAddress?.county === undefined ||
          previousAddress?.county.length < 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_FIELD,
          path: ['previousAddress.county'],
        });
      }

      /**
       * previousEmploymentDetails is also a conditionally-rendered field.
       *
       * We compare it to employmentDetails.timeWithEmployer.years to deterine when to trigger a validation error.
       */
      const displayPreviousEmployment =
        Number(employmentDetails.timeWithEmployer.years) <=
        THRESHOLD_TO_SHOW_PREVIOUS_EMPLOYMENT_FIELDS_IN_YEARS;

      if (
        displayPreviousEmployment &&
        (previousEmploymentDetails?.status === undefined ||
          previousEmploymentDetails.status.length < 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_FIELD,
          path: ['previousEmploymentDetails.status'],
        });
      }

      if (
        displayPreviousEmployment &&
        (previousEmploymentDetails?.previousOccupation === undefined ||
          previousEmploymentDetails.previousOccupation.length < 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_FIELD,
          path: ['previousEmploymentDetails.previousOccupation'],
        });
      }

      if (
        displayPreviousEmployment &&
        (previousEmploymentDetails?.previousEmployerName === undefined ||
          previousEmploymentDetails.previousEmployerName.length < 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_FIELD,
          path: ['previousEmploymentDetails.previousEmployerName'],
        });
      }

      if (
        displayPreviousEmployment &&
        (previousEmploymentDetails?.timeWithPreviousEmployer.years ===
          undefined ||
          previousEmploymentDetails.timeWithPreviousEmployer.years.length < 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_FIELD,
          path: ['previousEmploymentDetails.timeWithPreviousEmployer.years'],
        });
      }
    },
  );

export {
  queryParameterSchema,
  mappedQueryParameterSchema,
  carFinanceInitialFormDataSchema,
  loanRequestSchema,
  loanResponseSchema,
  closebrothersFormSchema,
  bankofirelandFormSchema,
  hiddenFieldsSchema,
  loanDetailsSchema,
  sharedLoanDetailsSchema,
  carDetailsSchema,
  mockCarFinanceInitialFormData,
  mockLoanResponse,
};

export type QueryParameters = z.infer<typeof queryParameterSchema>;
export type Provider = z.infer<typeof providerSchema>;
export type LoanRequest = z.infer<typeof loanRequestSchema>;
export type InitialFormValues = z.infer<typeof carFinanceInitialFormDataSchema>;
export type LoanResponseSchema = z.infer<typeof loanResponseSchema>;
export type CloseBrothersForm = z.infer<typeof closebrothersFormSchema>;
export type BankOfIrelandForm = z.infer<typeof bankofirelandFormSchema>;
export type Form = CloseBrothersForm | BankOfIrelandForm;
export type FormPayloadSchema = Omit<Form, 'personalDetails'> & {
  personalDetails: Omit<Form['personalDetails'], 'dateOfBirth'> & {
    dateOfBirth: {
      day: string;
      month: string;
      year: string;
    };
  };
};
export type PersonalDetails = z.infer<typeof sharedPersonalDetailsSchema>;
export type CurrentAddress = z.infer<typeof sharedCurrentAddressSchema>;
export type BankingDetails = z.infer<typeof sharedBankingDetailsSchema>;
export type EmploymentDetails = z.infer<typeof sharedEmploymentDetailsSchema>;
export type PreviousAddress = z.infer<typeof sharedPreviousAddressSchema>;
export type LoanDetailsUserInput = z.infer<typeof loanDetailsUserInputSchema>;
export type LoanResponse = z.infer<typeof loanResponseSchema>;

export const ProviderDisplayName = {
  closebrothers: 'Close Brothers',
  bankofireland: 'Bank of Ireland',
};
